import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plus, FolderOpen, Check, Users, Settings2, Upload } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import * as frentesApi from '@/api/frentes.api';
import type { ChatEnLista, Frente } from '@/api/frentes.api';
import { hora12, fechaCorta, iniciales, nombreCorto } from '@/lib/athena';
import { Candado } from '@/components/athena/Candado';
import * as candado from '@/hooks/candado';

/**
 * CHATS — tus conversaciones, divididas como tú quieras.
 *
 * Esteban, 20-sep: *"no veo el chat en vivo dentro del programa... puedo
 * organizarlo por grupos de personas, dividir todo lo que soy de trabajo, todo
 * lo que son personales... la jerarquía se respete en todos los frentes que yo
 * abra. Ningún frente es más importante que otro."*
 *
 * ═══ LOS FRENTES SE DIBUJAN TODOS IGUAL ═══
 *
 * Mismo tamaño, misma fila, mismo peso tipográfico. El único que se ve distinto
 * es el que está abierto. Si "Trabajo" fuera más grande que "Familia", la
 * pantalla estaría contradiciendo lo que el dueño dijo de su propia vida.
 *
 * El color lo elige él; el programa no reparte importancia.
 *
 * ═══ "SIN CLASIFICAR" NO ES UN FRENTE ═══
 *
 * Es un filtro aparte, y existe para que nada quede invisible. Un chat sin
 * frente tiene que poder encontrarse, o el sistema de carpetas se convierte en
 * una forma de perder cosas.
 */

function Burbuja({ f, activo, onClick }: { f: Frente; activo: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        // Todos del mismo tamaño: la jerarquía la pone el dueño, no el diseño.
        'flex min-h-[44px] shrink-0 items-center gap-2 rounded-full border px-3.5 text-sm transition',
        activo
          ? 'border-transparent text-white'
          : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200',
      )}
      style={activo ? { backgroundColor: f.color } : undefined}
    >
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: activo ? '#fff' : f.color }}
      />
      {f.nombre}
      {typeof f.chats === 'number' && f.chats > 0 && (
        <span className={cn('text-xs', activo ? 'text-white/80' : 'text-slate-400')}>
          {f.chats}
        </span>
      )}
    </button>
  );
}

function FilaDeChat({
  c,
  frentes,
  onAbrir,
  onMover,
}: {
  c: ChatEnLista;
  frentes: Frente[];
  onAbrir: (id: string) => void;
  onMover: (chatId: string, frenteId: string, poner: boolean) => void;
}) {
  const [menu, setMenu] = useState(false);
  const enEste = new Set(c.frentes.map((f) => f.id));

  return (
    <div className="border-b border-slate-100 last:border-0 dark:border-slate-800">
      <div className="flex min-h-[64px] items-center gap-3 py-2.5">
        <button
          onClick={() => onAbrir(c.id)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-100 text-sm font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {c.esGrupo ? <Users className="h-4 w-4" /> : iniciales(c.nombre)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {nombreCorto(c.nombre)}
              </span>
              {c.lastMessageAt && (
                <span className="shrink-0 text-[11px] text-slate-400">
                  {new Date(c.lastMessageAt).toDateString() === new Date().toDateString()
                    ? hora12(c.lastMessageAt)
                    : fechaCorta(c.lastMessageAt)}
                </span>
              )}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="min-w-0 flex-1 truncate text-xs text-slate-500 dark:text-slate-400">
                {c.ultimo
                  ? `${c.ultimo.mio ? 'Tú: ' : ''}${c.ultimo.texto || '(archivo)'}`
                  : 'Sin mensajes'}
              </span>
              {c.frentes.map((f) => (
                <span
                  key={f.id}
                  title={f.nombre}
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: f.color }}
                />
              ))}
            </span>
          </span>
        </button>

        <button
          onClick={() => setMenu((v) => !v)}
          aria-label="Mover de frente"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <FolderOpen className="h-4 w-4" />
        </button>
      </div>

      {/* Un chat puede estar en varios frentes: son casillas, no una lista de
          opción única. El primo que además es cliente es de los dos. */}
      {menu && (
        <div className="flex flex-wrap gap-1.5 pb-3">
          {frentes.map((f) => {
            const puesto = enEste.has(f.id);
            return (
              <button
                key={f.id}
                onClick={() => onMover(c.id, f.id, !puesto)}
                className={cn(
                  'flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 text-xs transition',
                  puesto
                    ? 'border-transparent text-white'
                    : 'border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300',
                )}
                style={puesto ? { backgroundColor: f.color } : undefined}
              >
                {puesto && <Check className="h-3 w-3" />}
                {f.nombre}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Chats() {
  const [params, setParams] = useSearchParams();
  const [buscar, setBuscar] = useState('');
  const navegar = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const frenteId = params.get('frente') ?? undefined;
  const sinFrente = params.get('sin') === '1';
  // El candado se comprueba en cada render: `estaAbierto` mira la hora, asi
  // que a los dos minutos vuelve a pedir la llave sin necesidad de un reloj.
  const [desbloqueado, setDesbloqueado] = useState(candado.estaAbierto());

  const frentes = useQuery({ queryKey: ['frentes'], queryFn: frentesApi.getFrentes });
  const chats = useQuery({
    queryKey: ['chats', frenteId, sinFrente, buscar],
    queryFn: () =>
      frentesApi.getChats({
        frenteId,
        sinFrente: sinFrente || undefined,
        buscar: buscar.trim() || undefined,
        limite: 150,
      }),
  });

  const mover = useMutation({
    mutationFn: ({ chatId, frenteId: f, poner }: { chatId: string; frenteId: string; poner: boolean }) =>
      frentesApi.moverChatDeFrente(chatId, f, poner),
    onSuccess: (_d, v) => {
      toast.success(v.poner ? 'Movido al frente' : 'Sacado del frente');
      void qc.invalidateQueries({ queryKey: ['chats'] });
      void qc.invalidateQueries({ queryKey: ['frentes'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const lista = frentes.data?.frentes ?? [];
  const elFrente = lista.find((f) => f.id === frenteId);
  // Si el frente tiene llave y el candado esta cerrado, no se dibuja NADA de
  // su contenido: se dibuja el candado. Esconder las filas con CSS dejaria los
  // nombres en el HTML.
  const pideLlave = Boolean(elFrente?.conCandado) && !desbloqueado;

  const elegir = (id?: string, sin = false) => {
    const p = new URLSearchParams();
    if (sin) p.set('sin', '1');
    else if (id && id !== frenteId) p.set('frente', id);
    setParams(p, { replace: true });
  };

  if (pideLlave && elFrente) {
    return (
      <Candado
        nombreDelFrente={elFrente.nombre}
        onAbierto={() => setDesbloqueado(true)}
        onCancelar={() => elegir(undefined)}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Chats</h1>
          <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
            Tus conversaciones, divididas como tú quieras.
          </p>
        </div>
        <div className="flex shrink-0 gap-1">
          {/* Traer un historial va ACA y no en el menu: se busca cuando se
              esta mirando los chats y se ve que falta uno. */}
          <button
            onClick={() => navegar('/importar')}
            className="grid h-11 w-11 place-items-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Traer un historial"
            title="Traer un historial de WhatsApp"
          >
            <Upload className="h-5 w-5" />
          </button>
          <button
            onClick={() => navegar('/frentes')}
            className="grid h-11 w-11 place-items-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Administrar frentes"
          >
            <Settings2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* LOS FRENTES. Todos del mismo tamaño, en una sola fila que rueda. */}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
        <button
          onClick={() => elegir(undefined)}
          className={cn(
            'min-h-[44px] shrink-0 rounded-full border px-3.5 text-sm transition',
            !frenteId && !sinFrente
              ? 'border-transparent bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
              : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200',
          )}
        >
          Todos
        </button>

        {lista.map((f) => (
          <Burbuja key={f.id} f={f} activo={f.id === frenteId} onClick={() => elegir(f.id)} />
        ))}

        <button
          onClick={() => elegir(undefined, true)}
          className={cn(
            'min-h-[44px] shrink-0 rounded-full border border-dashed px-3.5 text-sm transition',
            sinFrente
              ? 'border-transparent bg-slate-600 text-white'
              : 'border-slate-300 text-slate-500 dark:border-slate-600 dark:text-slate-400',
          )}
        >
          Sin clasificar
        </button>

        <button
          onClick={() => navegar('/frentes')}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-dashed border-slate-300 text-slate-400 dark:border-slate-600"
          aria-label="Nuevo frente"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          placeholder="Buscar por nombre o teléfono…"
          className="min-h-[44px] w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
      </div>

      <Card>
        <CardBody className="space-y-0">
          {chats.isPending || frentes.isPending ? (
            <Spinner />
          ) : chats.isError ? (
            <p className="py-6 text-center text-sm text-red-600 dark:text-red-300">
              No se pudieron leer tus chats.
            </p>
          ) : (chats.data?.chats.length ?? 0) === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">
              {buscar
                ? `Nada con "${buscar}".`
                : sinFrente
                  ? 'Todos tus chats están clasificados.'
                  : frenteId
                    ? 'Este frente todavía no tiene chats. Muévelos con el botón de la carpeta.'
                    : 'Todavía no hay conversaciones. Aparecen cuando conectes tu WhatsApp.'}
            </p>
          ) : (
            chats.data?.chats.map((c) => (
              <FilaDeChat
                key={c.id}
                c={c}
                frentes={lista}
                onAbrir={(id) => navegar(`/chats/${id}`)}
                onMover={(chatId, f, poner) => mover.mutate({ chatId, frenteId: f, poner })}
              />
            ))
          )}
        </CardBody>
      </Card>
    </div>
  );
}
